/**
 * Pure PII/secret scrubbers for Sentry events + breadcrumbs.
 *
 * NO `@sentry/react` import on purpose, so this stays unit-testable without the
 * SDK or a browser. Wired into `Sentry.init({ beforeSend, beforeBreadcrumb })`
 * in `sentry-impl.ts`. Defensive layer: the current app does not knowingly send
 * PII (`sendDefaultPii` is false), but auth tokens can ride along in URLs
 * (Supabase implicit-flow `#access_token=...`), Authorization headers, or free
 * text - this strips them before any event leaves the browser.
 *
 * Mutates the passed object in place AND returns it (ergonomic for both the
 * Sentry callbacks and tests).
 */

const REDACTED = '[REDACTED]';

// Query/hash param names whose VALUES must never reach Sentry.
const SENSITIVE_PARAMS = new Set([
  'access_token',
  'refresh_token',
  'provider_token',
  'provider_refresh_token',
  'id_token',
  'token',
  'code',
  'apikey',
  'api_key',
  'key',
  'password',
  'secret',
]);

// Request/breadcrumb header names to redact entirely.
const SENSITIVE_HEADERS = new Set([
  'authorization',
  'apikey',
  'x-api-key',
  'cookie',
  'set-cookie',
]);

// JWT-ish token: three base64url segments. Covers Supabase access/refresh
// tokens and Apple client secrets embedded in free text.
const JWT_RE = /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;

// Opaque Supabase secrets that are NOT JWTs and so slip past JWT_RE:
// personal access tokens (sbp_…) and the new-format secret API keys
// (sb_secret_…). Publishable keys (sb_publishable_…) are safe to expose, so
// they are deliberately not matched.
const OPAQUE_SECRET_RE = /\b(?:sbp_[A-Za-z0-9]{16,}|sb_secret_[A-Za-z0-9]{16,})\b/g;

/** Replace any JWT-looking or opaque-secret substrings with [REDACTED]. */
export function redactJwts(s: string): string {
  return s.replace(JWT_RE, REDACTED).replace(OPAQUE_SECRET_RE, REDACTED);
}

// `?key=val`, `#key=val`, `&key=val` - capture separator + key + value.
const PARAM_RE = /([?#&])([\w.-]+)=([^&#\s]*)/g;

/**
 * Redact sensitive query + hash params in a URL-ish OR bare-query-string value
 * (best-effort), then strip any embedded JWT/opaque secrets.
 *
 * A leading `&` is prepended before matching so a value that *starts* with
 * `key=...` (a bare query string such as a breadcrumb's `http.query`, with no
 * `?`/`#`/`&` in front) is matched too; it is stripped back off afterwards.
 * Full URLs keep their own separators and are unaffected.
 */
export function redactUrl(url: string): string {
  const out = ('&' + url)
    .replace(PARAM_RE, (match, sep: string, key: string) =>
      SENSITIVE_PARAMS.has(key.toLowerCase()) ? `${sep}${key}=${REDACTED}` : match,
    )
    .replace(/^&/, '');
  return redactJwts(out);
}

/** Redact a bare `a=b&c=d` query string (delegates to redactUrl). */
function redactQueryString(qs: string): string {
  return redactUrl(qs);
}

/**
 * Recursively redact string leaves in an arbitrary JSON-ish value, in place.
 * Strings are run through redactUrl (params + JWT/opaque secrets); numbers,
 * booleans and null pass through untouched. Depth-capped to guard against
 * pathologically deep payloads (Sentry events are JSON-serialisable, so no
 * cycles are expected, but the cap is a cheap safety net).
 */
function scrubDeep<T>(value: T, depth = 0): T {
  if (typeof value === 'string') return redactUrl(value) as unknown as T;
  if (depth >= 6) return value;
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) value[i] = scrubDeep(value[i], depth + 1);
    return value;
  }
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    for (const k of Object.keys(obj)) obj[k] = scrubDeep(obj[k], depth + 1);
    return value;
  }
  return value;
}

interface LooseBreadcrumb {
  message?: string;
  data?: Record<string, unknown> & { url?: string };
  [k: string]: unknown;
}

interface LooseEvent {
  message?: string;
  request?: {
    url?: string;
    query_string?: string | unknown;
    headers?: Record<string, string>;
    cookies?: unknown;
    data?: unknown;
  };
  breadcrumbs?: LooseBreadcrumb[];
  extra?: Record<string, unknown>;
  [k: string]: unknown;
}

/**
 * Scrub a single breadcrumb in place. Passes `null` through unchanged.
 *
 * ALL string values in `data` are scrubbed (recursing into nested objects), not
 * just `url` - navigation breadcrumbs carry the URL under `from`/`to`, and fetch
 * breadcrumbs under `http.query` / `http.fragment`, all of which can hold
 * `#access_token=...`.
 */
export function scrubBreadcrumb<T extends LooseBreadcrumb | null>(b: T): T {
  if (!b) return b;
  if (typeof b.message === 'string') b.message = redactJwts(b.message);
  if (b.data && typeof b.data === 'object') scrubDeep(b.data);
  return b;
}

/** Scrub an event (request URL/headers/cookies, message, breadcrumbs, extra). */
export function scrubEvent<T extends LooseEvent>(event: T): T {
  const req = event.request;
  if (req) {
    if (typeof req.url === 'string') req.url = redactUrl(req.url);
    if (typeof req.query_string === 'string') {
      req.query_string = redactQueryString(req.query_string);
    }
    if (req.headers && typeof req.headers === 'object') {
      for (const k of Object.keys(req.headers)) {
        if (SENSITIVE_HEADERS.has(k.toLowerCase())) req.headers[k] = REDACTED;
      }
    }
    if (req.cookies != null) req.cookies = REDACTED;
    // `data` may be a string body or a structured object - recurse either way.
    if (req.data != null) req.data = scrubDeep(req.data);
  }

  if (typeof event.message === 'string') event.message = redactJwts(event.message);

  if (Array.isArray(event.breadcrumbs)) {
    for (const b of event.breadcrumbs) scrubBreadcrumb(b);
  }

  // Recurse nested objects/arrays in extra, not just string leaves.
  if (event.extra && typeof event.extra === 'object') scrubDeep(event.extra);

  return event;
}
